import { LitElement } from '@lit'

class NovaVariantPicker extends LitElement {
  static properties = {
    productUrl: { type: String, attribute: 'product-url' },
    sectionId: { type: String, attribute: 'section-id' }
  }

  connectedCallback() {
    this.activeFocusIndex = null
    super.connectedCallback()
    this._initializeElements()
    this._addEventListeners()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this._removeEventListeners()
  }

  _initializeElements() {
    this.$optionInputs = this.querySelectorAll('.js-options-input')
  }

  _addEventListeners() {
    for (const $input of this.$optionInputs) {
      $input.addEventListener('change', this._handleOptionChange)
    }

    document.addEventListener(
      'nova:product:render-variant-picker',
      this._handleVariantPickerUpdate,
    )
  }

  _removeEventListeners() {
    for (const $input of this.$optionInputs) {
      $input.removeEventListener('change', this._handleOptionChange)
    }

    document.removeEventListener(
      'nova:product:render-variant-picker',
      this._handleVariantPickerUpdate,
    )
  }

  _handleVariantPickerUpdate = (event) => {
    this.updateContent(event.detail.html, event.detail.valueIndex)
  }

  updateURL(selectedVariantId, productUrl) {
    const searchParams = new URLSearchParams(document.location.search)
    if (selectedVariantId) {
      searchParams.set('variant', selectedVariantId)
    } else {
      searchParams.delete('variant')
    }

    const url =
      searchParams.size > 0
        ? `${productUrl}?${searchParams.toString()}`
        : productUrl
    window.history.replaceState({}, '', url)
  }

  _handleOptionChange = (event) => {
    const optionType = event.target.name
    const { index, productUrl } = event.target.dataset;
    this.activeFocusIndex = index
    const selectedVariantId = Number.parseInt(event.target.dataset.variantId) || 0

    this.updateURL(selectedVariantId, productUrl)
    this._fetchProductSection(productUrl, selectedVariantId)
  }

  async _fetchProductSection(productUrl, selectedVariantId) {
    const params = `&variant=${selectedVariantId}`

    const response = await fetch(
      `${productUrl}?section_id=${this.sectionId}${params}`,
    )
    const responseText = await response.text()
    const sectionHtml = new DOMParser().parseFromString(
      responseText,
      'text/html',
    )

    const $variantPicker = sectionHtml.querySelector('nova-variant-picker')
    document.dispatchEvent(
      new CustomEvent('nova:product:render-variant-picker', {
        detail: {
          html: $variantPicker,
          valueIndex: this.activeFocusIndex,
        },
      }),
    )

    const $form = sectionHtml.querySelector('.js-product-qty-atc-wrapper')
    document.dispatchEvent(
      new CustomEvent('nova:product:render-form', {
        detail: { html: $form },
      }),
    )

    const $price = sectionHtml.querySelector('nova-price')
    document.dispatchEvent(
      new CustomEvent('nova:product:render-price', {
        detail: { html: $price },
      }),
    )

    const $quantity = sectionHtml.querySelector('nova-quantity-input')
    document.dispatchEvent(
      new CustomEvent('nova:product:render-quantity-input', {
        detail: { html: $quantity },
      }),
    )

    const $sideMark = sectionHtml.querySelector('.product-side-mark')
    if ($sideMark) {
      document.dispatchEvent(
        new CustomEvent('nova:product:render-side-mark', {
          detail: {
            visible: $sideMark.classList.contains('hidden')
          },
        }),
      )
    }

    const $customCalculator = sectionHtml.querySelector('nova-custom-calculator')
    document.dispatchEvent(
      new CustomEvent('nova:product:render-custom-calculator', {
        detail: { html: $customCalculator },
      }),
    )

    const $media = sectionHtml.querySelector('nova-product-gallery')
    document.dispatchEvent(
      new CustomEvent('nova:product:render-gallery', {
        detail: { html: $media },
      }),
    )

    const $title = sectionHtml.querySelector('.js-product-title')
    document.dispatchEvent(
      new CustomEvent('nova:product:render-title', {
        detail: { html: $title },
      }),
    )

    const $productComplementaryProducts = sectionHtml.querySelector('.js-product-complementary-products')
    document.dispatchEvent(
      new CustomEvent('nova:product:render-complementary-products', {
        detail: { html: $productComplementaryProducts },
      }),
    )
  }

  updateContent(content, valueIndex) {
    if (!content) return
    // Clear existing children
    while (this.firstChild) {
      this.removeChild(this.firstChild)
    }
    // Append new children
    while (content.firstChild) {
      this.appendChild(content.firstChild)
    }
    this.requestUpdate()

    this._initializeElements()
    this._addEventListeners()

    for (const $input of this.$optionInputs) {
      if($input.dataset.index === valueIndex)
        $input.focus()
    }
  }

  createRenderRoot() {
    return this
  }
}

customElements.define('nova-variant-picker', NovaVariantPicker)
